import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { SitesService } from '../../../services/sites.service';
import { ClientOnboardingService } from '../../../services/client-onboarding.service';
import { NotificationsService } from '../../../services/notifications.service';
import {
  BillingService,
  RevioServiceProduct,
} from '../../../services/billing.service';
import { ServiceAndLocation } from '@voice/utils';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CurrencyPipe } from '@angular/common';
import { WizardButtonsComponent } from './wizard-buttons.component';
import { ServiceFormComponent } from './service-form.component';
import { firstValueFrom } from 'rxjs';
import { downloadCSV } from '../../../utils/file-manager';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

const DEFAULT_PROVIDER = 'UDT';
const DEFAULT_SERVICE_TYPE = 'Hosted Voice Services';

@Component({
  selector: 'app-client-onboarding-services',
  standalone: true,
  templateUrl: './services-list.component.html',
  providers: [DialogService, ConfirmationService],
  imports: [
    TableModule,
    ButtonModule,
    CardModule,
    CurrencyPipe,
    WizardButtonsComponent,
    ConfirmDialogModule,
  ],
})
export class ServicesListComponent implements OnInit, OnChanges {
  constructor(
    private readonly dialogService: DialogService,
    private readonly sitesService: SitesService,
    private readonly clientOnboardingService: ClientOnboardingService,
    private readonly notificationService: NotificationsService,
    private readonly billingService: BillingService,
    private readonly confirmationService: ConfirmationService
  ) {}

  @Input() locations!: ServiceAndLocation[];
  @Input() onBoardingId?: number;
  @Input() domain?: string;
  @Input() parentCustomerId?: number;

  serviceProducts: RevioServiceProduct[] = [];
  loading = false;

  @Output() servicesReady = new EventEmitter<string>();

  async ngOnInit(): Promise<void> {
    if (!this.locations) {
      this.locations = [];
    }

    if (this.domain) {
      this.loading = true;
      this.sitesService.getSitesInDomain(this.domain).subscribe({
        next: (sites) => {
          if (this.locations.length === 0) {
            this.locations = sites.map((site) => ({
              companyName: site.site,
              provider: DEFAULT_PROVIDER,
              serviceType: DEFAULT_SERVICE_TYPE,
            }));
          } else {
            for (const location of this.locations) {
              location.provider ??= DEFAULT_PROVIDER;
              location.serviceType ??= DEFAULT_SERVICE_TYPE;
            }
          }
          this.loading = false;
        },
        error: (err) => {
          this.notificationService.error(err);
          this.loading = false;
        },
      });
    }

    if (this.parentCustomerId) {
      await this.loadRevioServiceProducts(this.parentCustomerId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['locations']) return;
    this.applyDefaultServiceValuesToLocations();
  }

  private applyDefaultServiceValuesToLocations(): void {
    if (!this.locations || this.locations.length === 0) return;

    const hasMissingDefaults = this.locations.some(
      (l) => !l.provider || !l.serviceType
    );
    if (!hasMissingDefaults) return;

    this.locations = this.locations.map((l) => ({
      ...l,
      provider: l.provider || DEFAULT_PROVIDER,
      serviceType: l.serviceType || DEFAULT_SERVICE_TYPE,
    }));
  }

  add(): void {
    const config: DynamicDialogConfig = {
      header: 'Add service',
      data: { record: undefined },
      modal: true,
      closable: true,
    };

    this.dialogService.open(ServiceFormComponent, config).onClose.subscribe({
      next: (added: ServiceAndLocation) => {
        if (added) {
          this.locations = [...this.locations, added];
        }
      },
    });
  }

  edit(service: ServiceAndLocation): void {
    const config: DynamicDialogConfig = {
      header: 'Edit service',
      data: { record: service },
      modal: true,
      closable: true,
    };

    this.dialogService.open(ServiceFormComponent, config).onClose.subscribe({
      next: (updated: ServiceAndLocation) => {
        if (updated) {
          this.locations = this.locations.map((l) =>
            l === service ? updated : l
          );
        }
      },
    });
  }

  delete(service: ServiceAndLocation): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove service "${service.companyName}"?`,
      header: 'Delete service',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.locations = this.locations.filter((l) => l !== service);
      },
    });
  }

  onWizardButtonClick(direction: string): void {
    if (!this.onBoardingId) {
      this.notificationService.error('No onboarding record selected').then();
      return;
    }

    if (this.locations.length === 0) {
      this.servicesReady.emit(direction);
      return;
    }

    this.clientOnboardingService
      .saveLocations(this.onBoardingId, this.locations)
      .subscribe({
        next: () => {
          this.notificationService
            .success('Services saved successfully')
            .then(() => this.servicesReady.emit(direction));
        },
      });
  }

  exportServices(): void {
    if (!this.locations?.length) {
      this.notificationService.error('No services to export');
      return;
    }

    const columns = [
      'companyName',
      'provider',
      'serviceType',
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'zipCode',
    ];

    downloadCSV(`services_${this.domain ?? 'export'}`, columns, this.locations);
  }

  private async loadRevioServiceProducts(parentCustomerId: number): Promise<void> {
    try {
      const sites = await firstValueFrom(
        this.billingService.revioGetCustomerSites(parentCustomerId)
      );

      const allProducts: RevioServiceProduct[] = [];
      for (const site of sites) {
        const siteProducts = await firstValueFrom(
          this.billingService.revioGetServiceProducts(site.customerId)
        );
        allProducts.push(...siteProducts);
      }

      this.serviceProducts = allProducts;
    } catch {
      this.notificationService.error(
        'Failed to load services and service products from Rev.io'
      );
    }
  }
}
